// The sentence a credentials save (or a resume) adds when it asks for a
// replay of the destination's credential failures (review S7, replay on
// reconnect).
//
// POST /api/destinations/:type and PATCH .../enabled answer with
// replay: { eligible, tooOld } (src/destinations/reconnect.ts). eligible is
// what the sweep will re-send, counted with the sweep's own predicate and
// cap; tooOld is what failed on the old credentials but is past the
// platform's re-send limit. Both are 0 when nothing was asked for, and
// eligible is 0 when this web service is in dry-run, so the note never
// announces sends that may not happen. The sweep starts a minute after the
// save and each re-sent event writes a "Sent on retry" row (delivery-note.ts).
//
// Recent activity filters by the event's original time and opens on the
// last 24 hours (Activity.tsx), and a re-sent event keeps its original time,
// so the note names a range that reaches back to it, and keeps reaching it
// for days after the save, when the merchant comes back to look: the range
// counts back from when Activity loads, and the oldest re-sent event is
// already up to a window old when the sweep runs. For every window of at
// most 7 days (Meta, TikTok and Pinterest 167 h, GA4 71 h, Klaviyo 6 h) that
// is Last 30 days, which covers them for more than three weeks; Last 7 days
// lost the oldest of a 167-hour window about an hour after the save
// (surface-4). Google Ads' 55-day window needs All time.

import { DESTINATION_NAMES } from "./delivery-note";

/** A count from the response, or 0 when it is missing or not a positive number. */
function count(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

type MaybeReplay = { replay?: { eligible?: unknown; tooOld?: unknown } | null } | null | undefined;

/**
 * The replay counts in a save or resume response body. Any other shape
 * (an older server, an empty body) reads as nothing to report: reading a
 * property of a string or number gives undefined, which count() reads as 0.
 */
export function replayCounts(resp: unknown): { eligible: number; tooOld: number } {
  const replay = (resp as MaybeReplay)?.replay;
  return { eligible: count(replay?.eligible), tooOld: count(replay?.tooOld) };
}

const fmt = (n: number) => n.toLocaleString("en-US");

/**
 * The Recent activity range (its label in Activity.tsx) that shows a re-sent
 * event of `type`, and still shows it days later. An unknown type gets Last
 * 30 days, like every type whose window is at most 7 days.
 */
function activityRange(type: string): string {
  return type === "google_ads" ? "All time" : "Last 30 days";
}

/**
 * What the save set in motion for `type`'s failed events, from the response
 * body, or null when it set nothing in motion.
 */
export function replayNote(type: string, resp: unknown): string | null {
  const { eligible, tooOld } = replayCounts(resp);
  const name = DESTINATION_NAMES[type] ?? "this destination";
  const sentences: string[] = [];
  if (eligible > 0) {
    const one = eligible === 1;
    sentences.push(
      `Re-sending ${fmt(eligible)} ${one ? "event" : "events"} that failed on the old credentials, starting in about a minute.`,
      one
        ? `It keeps its original time, so set Recent activity to ${activityRange(type)} to see it as Sent on retry.`
        : `They keep their original time, so set Recent activity to ${activityRange(type)} to see them as Sent on retry.`,
    );
    if (tooOld > 0) {
      sentences.push(`${fmt(tooOld)} more ${tooOld === 1 ? "is" : "are"} too old to re-send to ${name}.`);
    }
  } else if (tooOld > 0) {
    sentences.push(
      tooOld === 1
        ? `1 event that failed on the old credentials is too old to re-send to ${name}.`
        : `${fmt(tooOld)} events that failed on the old credentials are too old to re-send to ${name}.`,
    );
  }
  return sentences.length > 0 ? sentences.join(" ") : null;
}

/** `message` followed by the replay note, when the save set a replay in motion. */
export function withReplayNote(message: string, type: string, resp: unknown): string {
  const note = replayNote(type, resp);
  return note ? `${message} ${note}` : message;
}
